import { useState } from 'react';
import type { DecodedParameterNode } from './decoders';
import { decodedNodeToParamTree } from './decoders';
import { RawValue } from './raw-value/RawValue';
import { ValueDecodedCard } from './ValueDecodedCard';
import type { NetworkType, ParamTreeNode } from '@mls-wallet/shared-types';
import { HighLevelSolidityType } from '@mls-wallet/shared-types';
import type { AddressBookExactEntry } from '@/types/address-book';

/**
 * Display a decoded parameter in a human-readable format.
 * Container component that delegates to RawValue for rendering with graceful fallback.
 * Mirrors self-custody architecture: ParameterDecodedDataCard wraps RawValue components.
 *
 * @param param - The decoded parameter node to display
 * @param networkId - The network ID for address/contract lookups
 * @param contractAddress - Optional contract address for context
 * @param combinedParamNames - Names of other params used to decode this param's bytes data (for internal txs)
 * @param methodName - The method name of the parent transaction
 */
export function ParameterDecodedDataCard({
  param,
  networkId,
  contractAddress,
  combinedParamNames,
  methodName,
  addressBookEntryByAddress,
}: {
  param: DecodedParameterNode;
  networkId: NetworkType;
  contractAddress?: string | null;
  combinedParamNames?: string[];
  methodName?: string;
  addressBookEntryByAddress?: Map<string, AddressBookExactEntry>;
}) {
  // Try to convert to ParamTreeNode for RawValue display
  const parsedValue: ParamTreeNode | undefined = (() => {
    try {
      return decodedNodeToParamTree(param);
    } catch (_) {
      return undefined;
    }
  })();

  // If the value is decoded (internal transactions), show toggle
  const [showRaw, setShowRaw] = useState<boolean>(false);
  const hasValueDecoded = Boolean(
    param.valueDecoded &&
      (Array.isArray(param.valueDecoded) ? param.valueDecoded.length > 0 : true)
  );

  return (
    <div className="w-full px-3 py-2 border border-zinc-200 rounded-[8px]">
      {/* Header Section */}
      <div className="flex items-start justify-between gap-3 w-full">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-[14px] text-zinc-500">{param.name}</p>

          {/* Array element count */}
          {parsedValue?.highLevelType === HighLevelSolidityType.ARRAY &&
            parsedValue?.children && (
              <span className="text-xs text-zinc-500">
                ({parsedValue.children.length} item
                {parsedValue.children.length === 1 ? '' : 's'})
              </span>
            )}

          {/* Combined params indicator */}
          {Boolean(combinedParamNames?.length) && !showRaw && (
            <span className="text-xs text-zinc-400">
              Decoded using params: {combinedParamNames?.join(', ')}
            </span>
          )}
        </div>

        {/* Type and toggle button */}
        <div className="flex items-center gap-2">
          {hasValueDecoded && (
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setShowRaw(!showRaw)}
            >
              {showRaw ? 'See Parsed Data' : 'See Raw Data'}
            </button>
          )}
          <p className="text-[12px] text-zinc-500">{param.type}</p>
        </div>
      </div>

      {/* Content Section */}
      <div className="mt-2 w-full">
        {(() => {
          // Case 1: Has decoded internal transactions and user wants to see them
          if (hasValueDecoded && !showRaw && param.valueDecoded) {
            return (
              <div className="flex flex-col gap-2">
                {(Array.isArray(param.valueDecoded)
                  ? param.valueDecoded
                  : [param.valueDecoded]
                ).map((decoded: any, idx: number) => (
                  <ValueDecodedCard
                    key={idx}
                    decodedValue={decoded}
                    networkId={networkId}
                    index={idx}
                  />
                ))}
              </div>
            );
          }

          // Case 2: Display raw value using RawValue component (graceful fallback)
          if (parsedValue) {
            return (
              <RawValue
                paramTreeNode={parsedValue}
                networkId={networkId}
                contractAddress={contractAddress ?? null}
                inListOrObject={false}
                depth={1}
                paramName={param.name}
                {...(methodName ? { methodName } : {})}
                {...(addressBookEntryByAddress
                  ? { addressBookEntryByAddress }
                  : {})}
              />
            );
          }

          // Case 3: Fallback - unable to parse at all
          return (
            <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-700">
                Unable to decode parameter value
              </p>
              {param.value && (
                <pre className="mt-2 text-xs font-mono text-yellow-600 overflow-x-auto">
                  {JSON.stringify(param.value, null, 2)}
                </pre>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
